// @ts-nocheck -- Gadget Client types are stale; will resolve after ggt push regenerates types
/**
 * /about -- public about-us-and-trust page.
 *
 * Treats the page as fundamentally a trust narrative rather than a
 * corporate-history flier. Visual structure borrows the trade-page
 * (web/routes/_public.for.$trade.tsx) vocabulary: FadeInOnScroll per
 * section, alternating bg-stone-50/50 vs transparent, stat-band-style
 * callouts, and decorative backdrop icons on the credibility / build-
 * not-buy beats.
 *
 * Source-of-truth prose lives in docs/marketing/about.md so the copy
 * can be reviewed in plain markdown alongside the legal pages, and
 * iterated without diffing JSX.
 */

import type { Route } from "./+types/_public.about";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import { FadeInOnScroll } from "@/components/ui/FadeInOnScroll";
import {
  ArrowRight,
  ShieldCheck,
  Lock,
  CloudArrowUp,
  CheckCircle,
  GlobeHemisphereWest,
  HardDrives,
  KeyReturn,
  Quotes,
  Buildings,
  HouseSimple,
  Hammer,
  Robot,
  CreditCard,
  Sparkle,
  Wrench,
  ShareNetwork,
  UserCheck,
  Trash,
  PaperPlaneTilt,
  ArrowSquareOut,
  Lightbulb,
  UsersThree,
  Users,
  Ghost,
  ChartLineUp,
} from "@phosphor-icons/react";

export const meta: Route.MetaFunction = () => [
  { title: "About Manos -- and how we look after your business" },
  {
    name: "description",
    content:
      "Who builds Manos, where your data lives, what we do with it, and what happens when something goes wrong. Plain answers from people who've run small businesses themselves.",
  },
  { property: "og:title", content: "About Manos -- and how we look after your business" },
  {
    property: "og:description",
    content:
      "Who builds Manos, where your data lives, what we do with it, and what happens when something goes wrong.",
  },
  { property: "og:type", content: "website" },
  { property: "og:url", content: "https://manos.team/about" },
];

export const loader = async () => {
  return {};
};

export default function AboutPage() {
  return (
    <div className="overflow-hidden">
      {/* Hero --------------------------------------------------------- */}
      <section className="relative py-20 md:py-28 bg-gradient-to-b from-amber-50/60 to-transparent dark:from-amber-950/15 overflow-hidden">
        <div className="relative mx-auto max-w-3xl px-6 space-y-8">
          {/* Decorative backdrop -- inside the content column so its
              position is relative to the centred column, not the full
              section. Anchored to the column's right edge with
              translate-x-1/3 to push half of it past the edge so it
              reads as a watermark rather than a centred icon under
              the heading. */}
          <HouseSimple
            className="absolute right-0 top-0 translate-x-1/2 -translate-y-1/4 h-[28rem] w-[28rem] text-amber-500/[0.05] dark:text-amber-400/[0.07] pointer-events-none"
            weight="fill"
            aria-hidden="true"
          />
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
            About us, and how we look after your{" "}
            <span className="text-amber-600">business</span>
          </h1>
          <div className="space-y-5 text-lg text-muted-foreground leading-relaxed">
            <p>
              It's a Sunday evening. The job finished at four. The customer is
              still waiting for the invoice. The kitchen table has the laptop
              on it, the receipts in a pile, and half a cup of cold tea.
            </p>
            <p>
              This is the moment Manos was built for. And this is the page
              where we tell you who built it, what happens to your data, and
              what kind of outfit is on this end of the screen.
            </p>
          </div>
        </div>
      </section>

      {/* The people who built this ----------------------------------- */}
      <FadeInOnScroll>
        <section className="py-20">
          <div className="mx-auto max-w-6xl px-6">
            <div className="grid lg:grid-cols-[3fr_2fr] gap-12 lg:gap-20 items-start">
              <div className="space-y-6">
                <h2 className="text-3xl font-bold tracking-tight">
                  <UsersThree
                    className="relative inline-block h-7 w-7 text-amber-600 align-middle mr-2 -top-0.5"
                    weight="duotone"
                  />
                  The people who built this
                </h2>
                <p className="text-base leading-relaxed text-muted-foreground">
                  Manos is built and run by a small UK team. We've been at
                  this since{" "}
                  <strong className="text-foreground">2011</strong> (we know
                  that sounds recent. Even though it was actually 15 years
                  ago. Yes -- we feel old too).
                </p>
                <p className="text-base leading-relaxed text-muted-foreground">
                  Over those years, our work has architected platforms for{" "}
                  <strong className="text-foreground">Dyson</strong>,{" "}
                  <strong className="text-foreground">General Motors</strong>,{" "}
                  <strong className="text-foreground">
                    Formula One teams
                  </strong>
                  , and around{" "}
                  <strong className="text-foreground">
                    30 to 40 other businesses every year
                  </strong>{" "}
                  -- some you'd recognise, most you wouldn't, and that's
                  exactly how they liked it. Today, that work includes
                  engineering at one of the largest commerce platforms in the
                  world.
                </p>
                <p className="text-lg font-semibold text-foreground">
                  But the brand names aren't really the point.
                </p>
                <p className="text-base leading-relaxed text-muted-foreground">
                  The point is what running our{" "}
                  <em className="text-foreground not-italic font-semibold">
                    own
                  </em>{" "}
                  businesses actually looked like for nearly a decade. Two of
                  our own companies, never more than a handful of people. Every
                  hat that needed wearing: building the software, doing the
                  books, paying the bills, hiring the people, fixing things at
                  3am, sending invoices on a Sunday because nobody else would.
                </p>
                <p className="text-base leading-relaxed text-muted-foreground">
                  That's the experience that built Manos. Not the brand-name
                  work, but the unrelenting weight of running a business where
                  every job comes back to you. Manos exists because we've lived
                  that, and because the right software should be doing the
                  boring half of it.
                </p>
                <p className="text-sm text-muted-foreground/80">
                  Registered in the UK as{" "}
                  <strong className="text-foreground">
                    Manos Holdings Limited
                  </strong>
                  . Not VC-backed. No advertising business model. No data
                  brokers. Built to last, not to flip.
                </p>
              </div>

              {/* Pull-quote / stats sidebar */}
              <div className="lg:sticky lg:top-24 space-y-4">
                <div className="relative rounded-2xl border-l-4 border-amber-500 bg-amber-50/50 dark:bg-amber-950/15 p-6 md:p-8">
                  <Quotes
                    className="absolute -top-3 -left-3 h-10 w-10 text-amber-500/40"
                    weight="fill"
                    aria-hidden="true"
                  />
                  <p className="text-xl md:text-2xl font-bold leading-snug">
                    Every hat.
                    <br />
                    Every weekend.
                    <br />
                    Every "the buck stops here."
                  </p>
                  <p className="text-sm font-semibold text-amber-700 dark:text-amber-400 mt-3">
                    For nearly a decade.
                  </p>
                </div>
                <StatCard
                  number="15"
                  unit="years"
                  label="Building software businesses depend on"
                />
                <StatCard
                  number="30-40"
                  unit="brands / year"
                  label="From household names to the ones who prefer privacy"
                />
              </div>
            </div>
          </div>
        </section>
      </FadeInOnScroll>

      {/* Why we knew the bar had to be high -------------------------- */}
      <FadeInOnScroll>
        <section className="py-20 bg-amber-50/40 dark:bg-amber-950/10 relative overflow-hidden">
          <div className="relative mx-auto max-w-3xl px-6 space-y-6">
            {/* Decorative backdrop -- inside the content column,
                anchored to its left edge with -translate-x-1/3 so the
                icon hangs partway off into the section margin. */}
            <ShieldCheck
              className="absolute left-0 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[24rem] w-[24rem] text-amber-500/[0.06] dark:text-amber-400/[0.08] pointer-events-none"
              weight="fill"
              aria-hidden="true"
            />
            <h2 className="text-3xl font-bold tracking-tight">
              <ShieldCheck
                className="relative inline-block h-7 w-7 text-amber-600 align-middle mr-2 -top-0.5"
                weight="duotone"
              />
              Why we knew the bar had to be high
            </h2>
            <p className="text-base leading-relaxed text-muted-foreground">
              When you sign up to Manos, you start handing us your customer
              list, your job history, your prices, your messages. That's your
              livelihood.
            </p>
            <p className="text-base leading-relaxed text-muted-foreground">
              Our background means we don't get to hand-wave the security
              question. We've built systems that processed payments for global
              brands. We know exactly what's at stake when a database leaks, a
              backup fails, or an upgrade goes sideways at 6pm on a Friday.
            </p>
            <p className="text-base leading-relaxed text-muted-foreground">
              So Manos is built on the same kind of infrastructure those
              projects ran on, with the same standards.
            </p>
            <Callout
              icon={<Sparkle className="h-5 w-5" weight="fill" />}
              title="What that means in plain English"
              body="The bits underneath Manos are the same bits underneath major banks, government services (the good ones, that work), and global retailers. The bar didn't get lowered because we're serving small businesses."
            />
          </div>
        </section>
      </FadeInOnScroll>

      {/* Your data, on infrastructure trusted by banks --------------- */}
      <FadeInOnScroll>
        <section className="py-20">
          <div className="mx-auto max-w-6xl px-6 space-y-10">
            <div className="max-w-3xl space-y-3">
              <h2 className="text-3xl font-bold tracking-tight">
                <Buildings
                  className="relative inline-block h-7 w-7 text-amber-600 align-middle mr-2 -top-0.5"
                  weight="duotone"
                />
                Your data, on infrastructure trusted by banks
              </h2>
              <p className="text-base text-muted-foreground leading-relaxed">
                Software security has a lot of jargon. Here are the bits that
                actually matter, and what each one means in real words.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-5">
              <SecurityCard
                icon={<ShareNetwork className="h-6 w-6" weight="duotone" />}
                title="Encrypted in transit"
                body="Every time your data moves between your phone, your computer, and our servers, it travels through a locked tunnel -- the same kind of encryption your online banking uses. Anyone listening on the network sees gibberish."
              />
              <SecurityCard
                icon={<Lock className="h-6 w-6" weight="duotone" />}
                title="Encrypted at rest"
                body="When your data is sitting on disk, it's scrambled. The same standard governments use to protect classified information. Without the right keys, the data is unreadable -- even to someone who walked into the data centre with a hard drive."
              />
              <SecurityCard
                icon={<Buildings className="h-6 w-6" weight="duotone" />}
                title="Hosted on infrastructure used by banks"
                body={
                  <>
                    Manos runs on{" "}
                    <strong className="text-foreground">Google Cloud</strong>.
                    Their data centres are independently audited every year
                    against two big international standards:{" "}
                    <strong className="text-foreground">ISO 27001</strong> and{" "}
                    <strong className="text-foreground">SOC 2</strong>.
                    Independent auditors come in, check the locks, the access
                    logs, the incident response, the staff vetting, the
                    physical security, the server-room cameras. They write a
                    report. They publish it. The same checks are done for{" "}
                    <strong className="text-foreground">Stripe</strong> -- you
                    know, the folks that handle payments for{" "}
                    <strong className="text-foreground">Amazon</strong>,{" "}
                    <strong className="text-foreground">BMW</strong>, and{" "}
                    <strong className="text-foreground">Microsoft</strong>.
                  </>
                }
              />
              <SecurityCard
                icon={<ShieldCheck className="h-6 w-6" weight="duotone" />}
                title="Cloudflare in front of the door"
                body={
                  <>
                    Before any request reaches our servers, it passes through{" "}
                    <strong className="text-foreground">Cloudflare</strong>,
                    who absorb attacks and filter out malicious traffic. The
                    same company most major UK businesses use as their first
                    line of defence.
                  </>
                }
              />
              <SecurityCard
                icon={<CloudArrowUp className="h-6 w-6" weight="duotone" />}
                title="Backed up daily"
                body="If something ever went catastrophically wrong, we can rebuild from yesterday's backup. We don't just hope this works. We test it."
              />
              <SecurityCard
                icon={<KeyReturn className="h-6 w-6" weight="duotone" />}
                title="No password to leak"
                body="You sign in with Google or with a one-time link sent to your email. There's no password database for anyone to steal. If your phone is lost, your sign-in dies with the email or Google account, not with us."
              />
            </div>
          </div>
        </section>
      </FadeInOnScroll>

      {/* Will it be there when I need it? ---------------------------- */}
      <FadeInOnScroll>
        <section className="py-20 bg-stone-50/60 dark:bg-secondary/20">
          <div className="mx-auto max-w-3xl px-6 space-y-8">
            <h2 className="text-3xl font-bold tracking-tight">
              <ChartLineUp
                className="relative inline-block h-7 w-7 text-amber-600 align-middle mr-2 -top-0.5"
                weight="duotone"
              />
              Will it be there when I need it?
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <StatCard
                number="99.9%"
                unit="uptime target"
                label="Roughly eight hours of downtime over a whole year"
                accent
              />
              <StatCard
                number="< 60s"
                unit="alert response"
                label="We get paged within seconds when something goes wrong"
                accent
              />
            </div>
            <p className="text-base leading-relaxed text-muted-foreground">
              In practice we run better than the target, but we'd rather
              under-promise. When something does go wrong:
            </p>
            <ul className="space-y-3">
              <BulletItem>
                We get an alert within seconds.
              </BulletItem>
              <BulletItem>
                Most issues are fixed before you notice. The status page tells
                you about the rest.
              </BulletItem>
              <BulletItem>
                Your data isn't stuck behind one machine. It's replicated and
                backed up across multiple data centres.
              </BulletItem>
            </ul>
            <p className="text-base leading-relaxed text-muted-foreground">
              If your phone's internet drops mid-quote, the app keeps your
              form open. When you're back online, it sends.
            </p>
          </div>
        </section>
      </FadeInOnScroll>

      {/* Where your data lives --------------------------------------- */}
      <FadeInOnScroll>
        <section className="py-20">
          <div className="mx-auto max-w-3xl px-6 space-y-6">
            <h2 className="text-3xl font-bold tracking-tight">
              <GlobeHemisphereWest
                className="relative inline-block h-7 w-7 text-amber-600 align-middle mr-2 -top-0.5"
                weight="duotone"
              />
              Where your data lives
            </h2>
            <p className="text-base leading-relaxed text-muted-foreground">
              Your data sits primarily on{" "}
              <strong className="text-foreground">
                Google Cloud servers in Canada
              </strong>
              . The UK government, the EU, and Canada's own privacy regulators
              all formally recognise Canada as a safe place to hold personal
              data -- a status given to only a handful of countries with
              comparably strong legal protections in place.
            </p>
            <Callout
              icon={
                <span
                  className="text-base leading-none"
                  role="img"
                  aria-label="Canadian flag"
                >
                  🇨🇦
                </span>
              }
              title="Why Canada?"
              body="Our customers are spread across the UK, the US, and Europe. Canada is the spot everyone's regulators are happy with: trusted by the UK and EU as legally equivalent to their own protections, next door to the US for fast service across North America, and not flagged as a problem by anyone in between. A sensible middle ground."
              tone="amber"
            />
            <p className="text-base leading-relaxed text-muted-foreground">
              The services around it -- Cloudflare, our analytics, our billing
              infrastructure -- run from{" "}
              <strong className="text-foreground">
                edge locations all over the world
              </strong>
              , so the app stays fast for you whether you're in Cardiff,
              Calgary, or Sacramento. The contracts behind every one of them
              include the strictest cross-border data protections that UK and
              European regulators require, so the level of legal protection
              you get at home travels with your data wherever it sits.
            </p>
          </div>
        </section>
      </FadeInOnScroll>

      {/* We build it ourselves, where we can ------------------------- */}
      <FadeInOnScroll>
        <section
          id="build-not-buy"
          className="py-20 bg-amber-50/40 dark:bg-amber-950/10 relative overflow-hidden scroll-mt-20"
        >
          <div className="relative mx-auto max-w-3xl px-6 space-y-6">
            {/* Decorative backdrop -- inside the content column,
                anchored to its right edge with translate-x-1/3 so it
                hangs partway off into the section margin. */}
            <Hammer
              className="absolute right-0 top-1/2 translate-x-1/2 -translate-y-1/2 h-[20rem] w-[20rem] text-amber-500/[0.05] dark:text-amber-400/[0.08] pointer-events-none rotate-12"
              weight="fill"
              aria-hidden="true"
            />
            <h2 className="text-3xl font-bold tracking-tight">
              <Hammer
                className="relative inline-block h-7 w-7 text-amber-600 align-middle mr-2 -top-0.5"
                weight="duotone"
              />
              We build it ourselves, where we can
            </h2>
            <p className="text-2xl md:text-3xl font-extrabold leading-snug">
              We follow one guiding principle:{" "}
              <span className="text-amber-700 dark:text-amber-400">
                build, not buy.
              </span>
            </p>
            <p className="text-base leading-relaxed text-muted-foreground">
              Where Manos can build it in-house, we do. We only bring in a
              third party when they genuinely do something better than we
              could -- regulated payment processing, global data centres, the
              platform layer that keeps the app running. Every other piece of
              Manos is built by us.
            </p>
          </div>
        </section>
      </FadeInOnScroll>

      {/* Who can see your data --------------------------------------- */}
      <FadeInOnScroll>
        <section className="py-20">
          <div className="mx-auto max-w-3xl px-6 space-y-6">
            <h2 className="text-3xl font-bold tracking-tight">
              <UserCheck
                className="relative inline-block h-7 w-7 text-amber-600 align-middle mr-2 -top-0.5"
                weight="duotone"
              />
              Who can see your data
            </h2>
            <div className="overflow-hidden rounded-xl border">
              <table className="w-full text-sm">
                <thead className="bg-stone-50 dark:bg-secondary/30">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold">Who</th>
                    <th className="text-left px-4 py-3 font-semibold">When</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  <tr>
                    <td className="px-4 py-3 font-bold">You</td>
                    <td className="px-4 py-3 text-muted-foreground">Always.</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-bold">
                      Your team{" "}
                      <span className="text-muted-foreground font-normal">
                        (Team plan)
                      </span>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      Only the parts you've shared.
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-bold">Us</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      Only when you ask for help, only the minimum we need to
                      fix what's wrong, and every look is logged.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-base text-muted-foreground">That's the list.</p>
            <Callout
              icon={<ShieldCheck className="h-5 w-5" weight="fill" />}
              title="The promise"
              body="We do not sell your data. We do not share it with advertisers. We do not let anyone train AI on it."
              tone="amber"
            />
            <p className="text-base leading-relaxed text-muted-foreground">
              A few services have to see your data to do their job:{" "}
              <strong className="text-foreground">Stripe</strong> sees
              payments,{" "}
              <strong className="text-foreground">Google Cloud</strong> stores
              the files,{" "}
              <strong className="text-foreground">Cloudflare</strong> routes
              the traffic, our{" "}
              <strong className="text-foreground">platform partner</strong>{" "}
              runs the application. Each is named in our privacy policy and
              bound by the same rules we are.
            </p>
          </div>
        </section>
      </FadeInOnScroll>

      {/* Your customers' data ---------------------------------------- */}
      <FadeInOnScroll>
        <section className="py-20 bg-stone-50/60 dark:bg-secondary/20">
          <div className="mx-auto max-w-3xl px-6 space-y-6">
            <h2 className="text-3xl font-bold tracking-tight">
              <Users
                className="relative inline-block h-7 w-7 text-amber-600 align-middle mr-2 -top-0.5"
                weight="duotone"
              />
              Your customers' data
            </h2>
            <p className="text-base leading-relaxed text-muted-foreground">
              When you store a customer on Manos, you're the one ultimately
              responsible for them. They're your customer. We're the processor
              doing the work on your behalf.
            </p>
            <p className="text-base leading-relaxed text-muted-foreground">
              Wherever you and your customers happen to be, the principle is
              the same: people have a right to know what's held about them, to
              have it corrected if it's wrong, and to have it deleted if they
              ask.
            </p>
            <p className="text-base leading-relaxed text-foreground font-semibold">
              That principle is written into law in most of the places we
              work:
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
              <CountryCard
                flag="🇬🇧 🇪🇺"
                flagLabel="UK and EU flags"
                title="UK and EU"
                body="GDPR."
              />
              <CountryCard
                flag="🇺🇸"
                flagLabel="US flag"
                title="United States"
                body="California (CCPA / CPRA) leads, with similar laws now active in Virginia, Colorado, Connecticut, Texas, Utah, and a growing list of others. There's no single federal version yet -- the specifics vary by state, but the spirit is the same."
              />
              <CountryCard
                flag="🇨🇦"
                flagLabel="Canadian flag"
                title="Canada"
                body="PIPEDA at the federal level, plus provincial laws like Quebec's Law 25."
              />
              <CountryCard
                flag="🇦🇺"
                flagLabel="Australian flag"
                title="Australia"
                body="The Privacy Act 1988 and the Notifiable Data Breaches scheme."
              />
            </div>
            <p className="text-base leading-relaxed text-muted-foreground">
              You don't need to memorise the acronyms. The plain version is
              the same everywhere:
            </p>
            <Callout
              icon={<UserCheck className="h-5 w-5" weight="fill" />}
              title="Your promise to your customers"
              body="If you delete a customer, we delete them. If they ever ask what's held about them, you can show them straight from the app."
              tone="amber"
            />
          </div>
        </section>
      </FadeInOnScroll>

      {/* What about AI? ---------------------------------------------- */}
      <FadeInOnScroll>
        <section className="py-20">
          <div className="mx-auto max-w-3xl px-6 space-y-6">
            <h2 className="text-3xl font-bold tracking-tight">
              <Robot
                className="relative inline-block h-7 w-7 text-amber-600 align-middle mr-2 -top-0.5"
                weight="duotone"
              />
              What about AI?
            </h2>
            <p className="text-base leading-relaxed text-muted-foreground">
              Manos has an AI helper called{" "}
              <strong className="text-foreground">Apprentice</strong>. It
              writes up jobs, drafts quotes, and chases invoices. Here's the
              deal:
            </p>
            <ul className="space-y-3">
              <BulletItem>
                <strong>
                  Your prompts and replies are not used to train anyone's
                  model.
                </strong>{" "}
                We pay per call. The AI provider does not keep what you send.
              </BulletItem>
              <BulletItem>
                <strong>We strip out personal details</strong> like names,
                addresses, and phone numbers before sending to the AI wherever
                the task allows.
              </BulletItem>
              <BulletItem>
                <strong>You can turn it off</strong> any time in Settings.
              </BulletItem>
            </ul>
            <p className="text-base leading-relaxed text-muted-foreground">
              We have plans to bring more capabilities into Manos over time --
              the kind of thing that genuinely makes your day easier. Each one
              will go through the same checks: the same privacy rules, the
              same standards, the same scrutiny.
            </p>
            <p className="text-base leading-relaxed text-muted-foreground">
              In keeping with our{" "}
              <a
                href="#build-not-buy"
                className="text-amber-700 dark:text-amber-400 hover:underline font-semibold"
              >
                build-not-buy principle
              </a>
              , anything new will plug into the infrastructure we've already
              built and verified. Same suppliers, same protections, same
              access controls. Nothing new for you to verify, nothing new to
              worry about.
            </p>
          </div>
        </section>
      </FadeInOnScroll>

      {/* Payments ---------------------------------------------------- */}
      <FadeInOnScroll>
        <section className="py-20 bg-stone-50/60 dark:bg-secondary/20">
          <div className="mx-auto max-w-3xl px-6 space-y-4">
            <h2 className="text-3xl font-bold tracking-tight">
              <CreditCard
                className="relative inline-block h-7 w-7 text-amber-600 align-middle mr-2 -top-0.5"
                weight="duotone"
              />
              Payments
            </h2>
            <p className="text-base leading-relaxed text-muted-foreground">
              Card payments go through{" "}
              <strong className="text-foreground">Stripe</strong> (you know
              -- the folks that handle payments for the likes of{" "}
              <strong className="text-foreground">Amazon</strong>,{" "}
              <strong className="text-foreground">BMW</strong>, and{" "}
              <strong className="text-foreground">Microsoft</strong>), the
              regulated company that handles the money. Card numbers never
              touch our servers. We only see what we need to: the amount, the
              last four digits, and whether it cleared.
            </p>
          </div>
        </section>
      </FadeInOnScroll>

      {/* What if it breaks? ------------------------------------------ */}
      <FadeInOnScroll>
        <section className="py-20">
          <div className="mx-auto max-w-6xl px-6 space-y-10">
            <div className="max-w-3xl space-y-3">
              <h2 className="text-3xl font-bold tracking-tight">
                <Wrench
                  className="relative inline-block h-7 w-7 text-amber-600 align-middle mr-2 -top-0.5"
                  weight="duotone"
                />
                What if it breaks?
              </h2>
              <p className="text-base text-muted-foreground">
                Three honest possibilities, three honest answers.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-5">
              <BreakageCard
                icon={<KeyReturn className="h-6 w-6" weight="duotone" />}
                title="You lose access"
                body="Forgotten email, lost phone. Drop us a line. We have ways of getting you back in."
              />
              <BreakageCard
                icon={<Wrench className="h-6 w-6" weight="duotone" />}
                title="An update breaks something for you"
                body="We push a fix the same day. We don't run experiments on live data without telling you."
              />
              <BreakageCard
                icon={<HardDrives className="h-6 w-6" weight="duotone" />}
                title="You think we've lost data"
                body="We can roll back to any point in the last seven days. We've never had to do this in earnest, but we test that we can."
              />
            </div>
          </div>
        </section>
      </FadeInOnScroll>

      {/* What if Manos went away? ------------------------------------ */}
      <FadeInOnScroll>
        <section className="py-20 bg-stone-50/60 dark:bg-secondary/20">
          <div className="mx-auto max-w-3xl px-6 space-y-6">
            <h2 className="text-3xl font-bold tracking-tight">
              <Ghost
                className="relative inline-block h-7 w-7 text-amber-600 align-middle mr-2 -top-0.5"
                weight="duotone"
              />
              What if Manos went away?
            </h2>
            <p className="text-base leading-relaxed text-muted-foreground">
              We don't plan to walk away. But you deserve to know the answer.
            </p>
            <p className="text-base leading-relaxed text-muted-foreground">
              If we ever wound down, you'd get at least{" "}
              <strong className="text-foreground">90 days' notice</strong>,
              and the export would keep working the entire time. You'd take
              everything with you. The data formats slot straight into Xero,
              QuickBooks, or any spreadsheet -- no translation layer needed.
            </p>
          </div>
        </section>
      </FadeInOnScroll>

      {/* If you decide to leave -------------------------------------- */}
      <FadeInOnScroll>
        <section className="py-20">
          <div className="mx-auto max-w-3xl px-6 space-y-6">
            <h2 className="text-3xl font-bold tracking-tight">
              <ArrowSquareOut
                className="relative inline-block h-7 w-7 text-amber-600 align-middle mr-2 -top-0.5"
                weight="duotone"
              />
              If you decide to leave
            </h2>
            <p className="text-base leading-relaxed text-muted-foreground">
              Go to{" "}
              <strong className="text-foreground">Settings → Export</strong>{" "}
              and download every job, invoice, client, and message as a
              spreadsheet. No notice. No questions. No "are you sure?" loop.
            </p>
            <p className="text-base leading-relaxed text-muted-foreground">
              If you simply cancel, we keep your data for{" "}
              <strong className="text-foreground">90 days</strong> in case you
              change your mind -- plenty of time to come back, log in, and
              pick up where you left off.
            </p>
            <p className="text-base leading-relaxed text-muted-foreground">
              If you'd rather we deleted everything sooner, just ask. We wipe
              it within{" "}
              <strong className="text-foreground">30 days</strong> of your
              request.
            </p>
            <Callout
              icon={<Trash className="h-5 w-5" weight="fill" />}
              title="Not a favour, a right"
              body="Account deletion is a legal right under UK GDPR and the equivalent laws elsewhere. Not something we're being generous about."
              tone="amber"
            />
          </div>
        </section>
      </FadeInOnScroll>

      {/* Got an idea? ----------------------------------------------- */}
      <FadeInOnScroll>
        <section className="py-20 bg-amber-50/40 dark:bg-amber-950/10 relative overflow-hidden">
          <div className="relative mx-auto max-w-3xl px-6 space-y-6">
            {/* Decorative backdrop -- left-anchored, hangs into the
                section margin. Matches the band treatment on the other
                principle sections (Why we knew / Build, not buy). */}
            <Lightbulb
              className="absolute left-0 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[20rem] w-[20rem] text-amber-500/[0.06] dark:text-amber-400/[0.08] pointer-events-none"
              weight="fill"
              aria-hidden="true"
            />
            <h2 className="text-3xl font-bold tracking-tight">
              <Lightbulb
                className="relative inline-block h-7 w-7 text-amber-600 align-middle mr-2 -top-0.5"
                weight="duotone"
              />
              Got an idea?
            </h2>
            <p className="text-base leading-relaxed text-muted-foreground">
              If there's a feature or function you'd love to see, we want to
              hear about it. Once you're signed in, there's a{" "}
              <strong className="text-foreground">Feedback</strong> link in
              your main menu for feature requests and "wouldn't it be great
              if..." ideas. Same inbox, same set of eyes.
            </p>
            <p className="text-base leading-relaxed text-muted-foreground">
              Manos exists to make your day easier, and we're always open to
              learning how to do that better.
            </p>
          </div>
        </section>
      </FadeInOnScroll>

      {/* Closing CTA ------------------------------------------------- */}
      <FadeInOnScroll>
        <section className="py-20 bg-gradient-to-b from-amber-600 to-amber-700 text-white">
          <div className="mx-auto max-w-3xl px-6 text-center space-y-6">
            <PaperPlaneTilt
              className="h-12 w-12 mx-auto opacity-80"
              weight="duotone"
            />
            <h2 className="text-3xl font-bold tracking-tight">
              Anything else
            </h2>
            <p className="text-lg text-amber-50 leading-relaxed">
              The contact form on this site comes straight through to us. We
              read every message.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
              <Button
                asChild
                size="lg"
                className="h-12 px-8 text-base bg-white text-amber-700 hover:bg-amber-50 font-bold"
              >
                <Link to="/contact">
                  Get in touch
                  <ArrowRight className="ml-2 h-4 w-4" weight="bold" />
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="h-12 px-8 text-base bg-transparent border-white/40 text-white hover:bg-white/10 hover:text-white"
              >
                <Link to="/sign-up">Try Manos free</Link>
              </Button>
            </div>
          </div>
        </section>
      </FadeInOnScroll>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Building blocks
// ---------------------------------------------------------------------------

function StatCard({
  number,
  unit,
  label,
  accent = false,
}: {
  number: string;
  unit: string;
  label: string;
  accent?: boolean;
}) {
  return (
    <div
      className={`rounded-xl p-5 ${
        accent
          ? "bg-amber-50/60 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900/40"
          : "bg-card border"
      }`}
    >
      <div className="flex items-baseline gap-1.5">
        <span className="text-3xl font-extrabold">{number}</span>
        <span className="text-sm font-semibold text-muted-foreground">
          {unit}
        </span>
      </div>
      <p className="text-sm text-muted-foreground mt-1 leading-snug">
        {label}
      </p>
    </div>
  );
}

function SecurityCard({
  icon,
  title,
  body,
}: {
  icon: React.ReactNode;
  title: string;
  body: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border bg-card p-6 space-y-3">
      <div className="flex items-center gap-2">
        <div className="text-amber-600">{icon}</div>
        <h3 className="font-bold text-lg">{title}</h3>
      </div>
      <div className="text-sm text-muted-foreground leading-relaxed">
        {body}
      </div>
    </div>
  );
}

function BulletItem({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-3 text-base text-muted-foreground leading-relaxed">
      <CheckCircle
        className="h-5 w-5 text-amber-600 shrink-0 mt-0.5"
        weight="fill"
      />
      <span className="text-foreground/90">{children}</span>
    </li>
  );
}

function Callout({
  icon,
  title,
  body,
  tone = "neutral",
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
  tone?: "neutral" | "amber";
}) {
  const toneClasses =
    tone === "amber"
      ? "bg-amber-50/70 dark:bg-amber-950/20 border-amber-300 dark:border-amber-700/50"
      : "bg-card border";
  return (
    <div className={`rounded-xl border ${toneClasses} p-5 space-y-2`}>
      <div className="flex items-center gap-2 text-amber-600">
        {icon}
        <p className="text-sm font-bold uppercase tracking-wider">{title}</p>
      </div>
      <p className="text-base leading-relaxed text-foreground/90">{body}</p>
    </div>
  );
}

function CountryCard({
  flag,
  flagLabel,
  title,
  body,
}: {
  flag?: string;
  flagLabel?: string;
  title: string;
  body: string;
}) {
  return (
    <div className="rounded-xl border bg-card p-5 space-y-2">
      <p className="font-bold text-foreground flex items-center gap-2">
        {flag ? (
          <span
            className="text-xl leading-none"
            role="img"
            aria-label={flagLabel ?? title}
          >
            {flag}
          </span>
        ) : null}
        <span>{title}</span>
      </p>
      <p className="text-sm text-muted-foreground leading-relaxed">{body}</p>
    </div>
  );
}

function BreakageCard({
  icon,
  title,
  body,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
}) {
  return (
    <div className="rounded-xl border bg-card p-6 space-y-3">
      <div className="flex items-center gap-3 text-amber-600">
        {icon}
      </div>
      <h3 className="font-bold text-base">{title}</h3>
      <p className="text-sm text-muted-foreground leading-relaxed">{body}</p>
    </div>
  );
}
